/**
 * The SDK's JSON types: what a cell's data, a row's attribute bag and a row's
 * metadata are on the wire.
 *
 * `Json` is any JSON value; `JsonObject` is a JSON object (a read-only string
 * map of `Json`). Both are the `@tento-core/json-reader` shapes, so a value
 * read with `JsonReader` is already one. They replace the `UntypedJson`
 * (`any`) the SDK's signatures used to carry: a caller now narrows a read
 * (`typeof`, `JsonReader.string(...)`, a decoder) instead of reading fields
 * off `any`.
 */
import type { Option } from "./option.ts";
import type { JsonReader, JsonValue } from "@tento-core/json-reader";

/** Any JSON value: string, number, boolean, `null`, array or object. */
export type Json = JsonValue;

/** A JSON object: a read-only string-keyed map of `Json` values. */
export type JsonObject = JsonReader.Object;

/**
 * Values that go with `Json`: `object`, `isObject`, `string`, `number`,
 * `boolean`, `child`, and 5 more.
 */
export namespace Json {
  /** `value` as a JSON object; `null` for any other JSON value. */
  export function object(value: Option<Json>): Option<JsonObject> {
    return isObject(value) ? value : null;
  }

  /** Whether `value` is a JSON object (not an array, not `null`). */
  export function isObject(value: Option<Json>): value is JsonObject {
    return typeof value === "object" && value !== null && !Array.isArray(value);
  }

  /** `obj[key]` when it is a string; `null` otherwise. */
  export function string(obj: Option<JsonObject>, key: string): Option<string> {
    const value = obj?.[key] ?? null;
    return typeof value === "string" ? value : null;
  }

  /** `obj[key]` when it is a finite number; `null` otherwise. */
  export function number(obj: Option<JsonObject>, key: string): Option<number> {
    const value = obj?.[key] ?? null;
    return typeof value === "number" && Number.isFinite(value) ? value : null;
  }

  /** `obj[key]` when it is a boolean; `null` otherwise. */
  export function boolean(obj: Option<JsonObject>, key: string): Option<boolean> {
    const value = obj?.[key] ?? null;
    return typeof value === "boolean" ? value : null;
  }

  /** `obj[key]` when it is a JSON object; `null` otherwise. */
  export function child(obj: Option<JsonObject>, key: string): Option<JsonObject> {
    return object(obj?.[key] ?? null);
  }

  /** `obj[key]` when it is an array; `null` otherwise. */
  export function array(obj: Option<JsonObject>, key: string): Option<readonly Json[]> {
    const value = obj?.[key] ?? null;
    return Array.isArray(value) ? value : null;
  }

  /** `obj[key]` when it is an array of strings; `null` otherwise. */
  export function strings(
    obj: Option<JsonObject>,
    key: string,
  ): Option<readonly string[]> {
    const list = array(obj, key);
    if (list == null) return null;
    const out: string[] = [];
    for (const item of list) {
      if (typeof item !== "string") return null;
      out.push(item);
    }
    return out;
  }

  /**
   * `obj[key]` when it is an object whose values are all finite numbers;
   * `null` otherwise.
   */
  export function numbers(
    obj: Option<JsonObject>,
    key: string,
  ): Option<Readonly<Record<string, number>>> {
    const map = child(obj, key);
    if (map == null) return null;
    const out: Record<string, number> = {};
    for (const [k, v] of Object.entries(map)) {
      if (typeof v !== "number" || !Number.isFinite(v)) return null;
      out[k] = v;
    }
    return out;
  }

  /** `obj[key]` when it is one of `allowed`; `null` otherwise. */
  export function oneOf<T extends string>(
    obj: Option<JsonObject>,
    key: string,
    allowed: readonly T[],
  ): Option<T> {
    const value = string(obj, key);
    return allowed.find((candidate) => candidate === value) ?? null;
  }

  /**
   * `value` as the JSON `JSON.stringify` writes for it: what a typed value
   * (an event with `DateTime`s, a timer record) is in a request body or a
   * cell's `data`. A `toJSON` method is honoured (a `DateTime` becomes its
   * ISO string), a non-finite number is `null`, and `undefined`, functions
   * and symbols are dropped from objects and are `null` in arrays and at the
   * top level. Always a fresh value: nothing of `value` is shared.
   */
  export function wire(value: unknown): Option<Json> {
    if (value === null || value === undefined) return null;
    if (typeof value === "string" || typeof value === "boolean") return value;
    if (typeof value === "number") return Number.isFinite(value) ? value : null;
    if (typeof value !== "object") return null;
    if ("toJSON" in value && typeof value.toJSON === "function") {
      const json: unknown = value.toJSON();
      return json === value ? null : wire(json);
    }
    if (Array.isArray(value)) {
      const out: Json[] = [];
      for (const item of value) out.push(wire(item) ?? null);
      return out;
    }
    const out: { [key: string]: Json } = {};
    for (const [k, v] of Object.entries(value)) {
      if (v === undefined || typeof v === "function" || typeof v === "symbol") continue;
      out[k] = wire(v) ?? null;
    }
    return out;
  }
}

/**
 * Decode an in-memory value the SDK holds untyped (alias props and
 * overrides) into JSON: strings, finite numbers, booleans, `null`, arrays
 * and plain objects are kept; anything else (`undefined`, functions,
 * `Map`s, class instances) is dropped from its container, and is `null`
 * at the top level. Package-internal — not on the barrel.
 */
export function jsonOf(value: unknown): Option<Json> {
  if (value === null) return null;
  if (typeof value === "string" || typeof value === "boolean") return value;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (Array.isArray(value)) {
    const out: Json[] = [];
    for (const item of value) {
      const json = jsonOf(item);
      out.push(json ?? null);
    }
    return out;
  }
  if (typeof value === "object") {
    const proto = Object.getPrototypeOf(value);
    if (proto !== Object.prototype && proto !== null) return null;
    const out: { [key: string]: Json } = {};
    // Keys, not `Object.entries`: no `[key, value]` pair per field (every decoded cell passes here).
    for (const k of Object.keys(value)) {
      const v: unknown = Reflect.get(value, k);
      if (v === undefined) continue;
      const json = jsonOf(v);
      if (json === null && v !== null) continue;
      out[k] = json ?? null;
    }
    return out;
  }
  return null;
}

/** `jsonOf(value)` when it is a JSON object; `null` otherwise. */
export function jsonObjectOf(value: unknown): Option<JsonObject> {
  return Json.object(jsonOf(value));
}
